import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import useSeo from '../lib/seo';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ArrowRight, Check, Copy, LoaderCircle, Mail, RotateCcw } from 'lucide-react';
import PageHero from '../components/site/PageHero';
import { sendLead } from '../lib/sendLead';

const EMAIL = 'contato@idealizer.com.br';
const EMPTY = { nome: '', email: '', celular: '', assunto: '', website: '' };

const digits = (v) => v.replace(/\D/g, '');
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
const celularOk = (v) => {
  const d = digits(v);
  return d.length === 10 || d.length === 11;
};
// Mesmo formato da página antiga: (xx) x xxxx-xxxx
const maskCelular = (v) => {
  const d = digits(v).slice(0, 11);
  if (!d) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d[2]} ${d.slice(3, 7)}-${d.slice(7)}`;
};

const RULES = {
  nome: (v) => (v.trim().length >= 2 ? null : 'Como podemos te chamar?'),
  email: (v) => (emailOk(v) ? null : 'E-mail inválido.'),
  celular: (v) => (celularOk(v) ? null : 'Celular inválido. Use o formato (xx) x xxxx-xxxx.'),
  assunto: (v) => (v.trim().length >= 3 ? null : 'Conte em poucas palavras o que você precisa.'),
};

function CopyEmail() {
  const [copied, setCopied] = useState(false);
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      window.location.href = `mailto:${EMAIL}`;
    }
  };

  return (
    <div className="ct-mail">
      <span className="ct-mail-icon" aria-hidden>
        <Mail size={22} strokeWidth={1.6} />
      </span>
      <div className="ct-mail-copy">
        <span className="ct-mail-label">Prefere escrever direto?</span>
        <a href={`mailto:${EMAIL}`} className="ct-mail-address">
          {EMAIL}
        </a>
      </div>
      <button type="button" className={`ct-copy ${copied ? 'is-copied' : ''}`} onClick={copy}>
        <span className="ct-copy-icon" aria-hidden>
          {copied ? <Check size={16} strokeWidth={2.5} /> : <Copy size={16} strokeWidth={2} />}
        </span>
        <span aria-live="polite">{copied ? 'Copiado' : 'Copiar'}</span>
      </button>
    </div>
  );
}

function Field({ id, label, error, ok, children }) {
  return (
    <div className={`cf-field ${error ? 'has-error' : ''} ${ok ? 'is-ok' : ''}`}>
      <label htmlFor={id}>{label}</label>
      <div className="cf-control">
        {children}
        <span className="cf-valid" aria-hidden>
          <Check size={14} strokeWidth={3} />
        </span>
      </div>
      {error && (
        <span className="cf-error" id={`${id}-err`} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

function ContactForm() {
  const [data, setData] = useState(EMPTY);
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const root = useRef(null);

  const errorOf = (k) => (touched[k] ? RULES[k](data[k]) : null);
  const valid = Object.keys(RULES).every((k) => !RULES[k](data[k]));

  // Troca entre formulário e confirmação: a nova vista entra com blur e leve subida.
  useLayoutEffect(() => {
    if (!root.current || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo('.cf-anim', { y: 16, autoAlpha: 0, filter: 'blur(6px)' }, { y: 0, autoAlpha: 1, filter: 'blur(0px)', duration: 0.7, ease: 'expo.out', stagger: 0.06, clearProps: 'filter' });
    }, root);
    return () => ctx.revert();
  }, [status === 'done']); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => {
    const v = k === 'celular' ? maskCelular(e.target.value) : e.target.value;
    setData((d) => ({ ...d, [k]: v }));
  };
  const blur = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ nome: true, email: true, celular: true, assunto: true });
    if (!valid || status === 'sending') return;
    if (data.website) {
      setStatus('done'); // bot caiu no campo invisível
      return;
    }
    setStatus('sending');
    try {
      await sendLead({
        source: 'contact',
        name: data.nome.trim(),
        email: data.email.trim(),
        phone: data.celular,
        message: data.assunto.trim(),
        page: window.location.pathname,
      });
      setStatus('done');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Envio do contato falhou:', err);
      setStatus('error');
    }
  };

  const reset = () => {
    setData(EMPTY);
    setTouched({});
    setStatus('idle');
  };

  if (status === 'done') {
    return (
      <div className="cf-card cf-done" ref={root} role="status">
        <span className="cf-done-icon cf-anim" aria-hidden>
          <Check size={30} strokeWidth={2.5} />
        </span>
        <h2 className="cf-done-title cf-anim">Mensagem enviada!</h2>
        <p className="cf-done-text cf-anim">Recebemos seu contato e vamos responder pelo e-mail ou celular que você informou.</p>
        <button type="button" className="pg-back cf-anim" onClick={reset}>
          <RotateCcw size={15} strokeWidth={2} aria-hidden /> Enviar outra mensagem
        </button>
      </div>
    );
  }

  return (
    <form className="cf-card" onSubmit={submit} noValidate ref={root}>
      <div className="cf-row">
        <div className="cf-anim">
          <Field id="nome" label="Nome" error={errorOf('nome')} ok={!RULES.nome(data.nome)}>
            <input id="nome" name="nome" type="text" autoComplete="name" value={data.nome} onChange={set('nome')} onBlur={blur('nome')} aria-invalid={!!errorOf('nome')} />
          </Field>
        </div>
      </div>
      <div className="cf-row cf-row--2">
        <div className="cf-anim">
          <Field id="email" label="E-mail" error={errorOf('email')} ok={emailOk(data.email)}>
            <input id="email" name="email" type="email" autoComplete="email" value={data.email} onChange={set('email')} onBlur={blur('email')} aria-invalid={!!errorOf('email')} />
          </Field>
        </div>
        <div className="cf-anim">
          <Field id="celular" label="Celular" error={errorOf('celular')} ok={celularOk(data.celular)}>
            <input id="celular" name="celular" type="tel" inputMode="tel" autoComplete="tel-national" placeholder="(xx) x xxxx-xxxx" value={data.celular} onChange={set('celular')} onBlur={blur('celular')} aria-invalid={!!errorOf('celular')} />
          </Field>
        </div>
      </div>
      <div className="cf-row">
        <div className="cf-anim">
          <Field id="assunto" label="Assunto" error={errorOf('assunto')} ok={!RULES.assunto(data.assunto)}>
            <textarea id="assunto" name="assunto" rows={5} maxLength={1500} value={data.assunto} onChange={set('assunto')} onBlur={blur('assunto')} aria-invalid={!!errorOf('assunto')} />
          </Field>
        </div>
      </div>

      {/* Honeypot: fora da tela e da navegação por teclado */}
      <input className="pw-hp" type="text" name="website" tabIndex={-1} autoComplete="off" value={data.website} onChange={set('website')} aria-hidden />

      {status === 'error' && (
        <p className="cf-alert cf-anim" role="alert">
          Não conseguimos enviar agora. Tente de novo ou escreva para <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
        </p>
      )}

      <div className="cf-foot cf-anim">
        <button type="submit" className={`btn-primary cf-send ${valid ? 'is-ready' : ''}`} disabled={status === 'sending'}>
          {status === 'sending' ? (
            <>
              <LoaderCircle className="pw-spin" size={18} strokeWidth={2.2} aria-hidden /> Enviando
            </>
          ) : (
            <>
              Enviar <ArrowRight size={18} strokeWidth={2} aria-hidden />
            </>
          )}
        </button>
        <p className="cf-legal">
          Usamos seus dados só para responder a este contato. <Link to="/politica-privacidade">Política de privacidade</Link>
        </p>
      </div>
    </form>
  );
}

function Contato() {
  useSeo({
    title: 'Contato | Idealizer',
    description: 'Fale com a Idealizer sobre o seu app ou plataforma web. Respondemos pelo e-mail ou celular que você informar.',
    path: '/contato',
  });

  return (
    <main className="pg contact-v3">
      <PageHero
        compact
        lines={['Vamos', { text: 'conversar.', accent: true }]}
        lede="Conte o que você precisa. A gente responde pelo e-mail ou pelo celular que você informar."
      />

      <section className="container ct-grid" aria-label="Formulário de contato">
        <ContactForm />
        <aside className="ct-aside">
          <CopyEmail />
          <dl className="ct-facts">
            <div>
              <dt>Empresa</dt>
              <dd>Idealizer LTDA</dd>
            </div>
            <div>
              <dt>CNPJ</dt>
              <dd>57.325.737/0001-00</dd>
            </div>
          </dl>
        </aside>
      </section>
    </main>
  );
}

export default Contato;
